import { useMemo, useState } from "react";
import {
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Spinner,
  TextField,
  Toggle
} from "@fluentui/react";
import { Formik, Form } from "formik";
import useAxios from "axios-hooks";
import Toast from "../../../Forms/Toast";
import ContentApiSecretsDialog from "./ContentApiSecretsDialog";
import ContentApiWhitelistFields from "./ContentApiWhitelistFields";
import ContentApiSelector from "./ContentApiSelector";
import FeatureSummary from "../../../Util/FeatureSummary";

const defaultValues = {
  cobrandId: null,
  contentApiOnOff: false,
  contentApiClientKey: "",
  contentApiPrivateSecretKey: "",
  contentApiKeyHash: "",
  contentApiApprovedContentOnly: false,
  whitelistIps: "",
  whitelistOrigins: ""
};

const parseListField = (value) =>
  (value || "")
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const generateClientId = () => {
  const firstDigit = Math.floor(Math.random() * 9) + 1;
  const rest = Array.from({ length: 17 }, () => Math.floor(Math.random() * 10)).join("");
  return `${firstDigit}${rest}`;
};

const generateClientSecret = () => {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from({ length: 40 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join("");
};

const generateApiKey = () => {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: 15 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join("");
};

const hashSha256 = async (input) => {
  if (!window.crypto?.subtle) {
    throw new Error("SHA-256 hashing is not supported in this browser.");
  }

  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

const ContentApiSettings = () => {
  const [initialValues, setInitialValues] = useState(defaultValues);
  const [showToast, setShowToast] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [generatedSecrets, setGeneratedSecrets] = useState(null);
  const [showSecretsDialog, setShowSecretsDialog] = useState(false);
  const [hashingApiKey, setHashingApiKey] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState("");
  const [copyFeedbackType, setCopyFeedbackType] = useState(MessageBarType.success);
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [selectedCobrandName, setSelectedCobrandName] = useState("");

  const [{ data: cobrands, loading: loadingCobrands }] = useAxios("/internal/cobrands?bustCache=true");
  const cobrandOptions = useMemo(
    () => (cobrands || []).map((c) => ({ value: c.cobrandId, label: c.name })),
    [cobrands]
  );

  const [{ loading: loadingSettings }, fetchSettings] = useAxios(
    { url: "/internal/cobrand/content-api/0" },
    { manual: true }
  );
  const [{ loading: saving }, saveSettings] = useAxios(
    { url: "/internal/cobrand/content-api", method: "POST" },
    { manual: true }
  );
  const [{ loading: uploadingSecrets }, uploadSecrets] = useAxios(
    { url: "/internal/cobrand/content-api/upload-secrets", method: "POST" },
    { manual: true }
  );

  const handleCobrandChange = async (option, setFieldValue) => {
    const cobrandId = option?.value ?? null;
    setSelectedCobrandName(option?.label ?? "");
    setFieldValue("cobrandId", cobrandId);
    setShowError(false);
    setErrorMessage("");

    if (!cobrandId) {
      setInitialValues(defaultValues);
      return;
    }

    setInitialValues({ ...defaultValues, cobrandId });

    try {
      const result = await fetchSettings({ url: `/internal/cobrand/content-api/${cobrandId}` });
      const data = result?.data || {};

      setInitialValues({
        cobrandId,
        contentApiOnOff: !!data.contentApiOnOff,
        contentApiClientKey: data.contentApiClientKey || "",
        contentApiPrivateSecretKey: data.contentApiPrivateSecretKey || "",
        contentApiKeyHash: data.contentApiKeyHash || "",
        contentApiApprovedContentOnly: !!data.contentApiApprovedContentOnly,
        whitelistIps: (data.whitelistIps || []).join("\n"),
        whitelistOrigins: (data.whitelistOrigins || []).join("\n")
      });
    } catch (err) {
      console.error(err);
      setInitialValues({ ...defaultValues, cobrandId });
      setShowError(true);
      setErrorMessage("Unable to load Content API settings. Please try again.");
    }
  };

  const buildSecretName = (type) => {
    const trimmed = (selectedCobrandName || "").trim();
    if (!trimmed) {
      return "";
    }
    const normalized = trimmed.replace(/\s+/g, "");
    return `ContentApi-${type}-${normalized}`;
  };

  const handleCopy = async (value) => {
    if (!value) {
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      setCopyFeedback("Copied to clipboard.");
      setCopyFeedbackType(MessageBarType.success);
      setTimeout(() => setCopyFeedback(""), 3000);
    } catch (err) {
      console.error(err);
      setCopyFeedback("Copy failed. Please copy manually.");
      setCopyFeedbackType(MessageBarType.error);
    }
  };

  const closeSecretsDialog = () => {
    setShowSecretsDialog(false);
    setGeneratedSecrets(null);
    setUploadMessage("");
    setUploadError("");
    setCopyFeedback("");
  };

  return (
    <div className="card-body">
      <h2 className="mb20">Content API Settings</h2>

      {showError && (
        <MessageBar messageBarType={MessageBarType.error} className="mb20">
          {errorMessage || "Something went wrong. Please try again."}
        </MessageBar>
      )}

      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting }) => {
          if (!values.cobrandId) {
            setShowError(true);
            setErrorMessage("Select a cobrand before saving.");
            return;
          }

          setShowError(false);
          setErrorMessage("");

          try {
            const payload = {
              cobrandId: values.cobrandId,
              contentApiOnOff: !!values.contentApiOnOff,
              contentApiClientKey: values.contentApiClientKey?.trim() || null,
              contentApiPrivateSecretKey: values.contentApiPrivateSecretKey?.trim() || null,
              contentApiKeyHash: values.contentApiKeyHash?.trim() || null,
              contentApiApprovedContentOnly: !!values.contentApiApprovedContentOnly,
              whitelistIps: parseListField(values.whitelistIps),
              whitelistOrigins: parseListField(values.whitelistOrigins)
            };

            const result = await saveSettings({ data: payload });
            if (result?.error) {
              throw result.error;
            }

            setShowToast(true);
          } catch (err) {
            console.error(err);
            setShowError(true);
            setErrorMessage("Unable to save Content API settings. Please try again.");
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, handleSubmit, setFieldValue, isSubmitting }) => {
          const disableInputs = loadingSettings || saving || loadingCobrands;
          const selectValue =
            cobrandOptions.find((option) => option.value === values.cobrandId) || null;

          return (
            <>
              <Form onSubmit={handleSubmit}>
                <ContentApiSelector
                  options={cobrandOptions}
                  value={selectValue}
                  onChange={(option) => handleCobrandChange(option, setFieldValue)}
                  loading={loadingCobrands || loadingSettings}
                  disabled={loadingCobrands || saving}
                />

                <div className="mb10">
                  <Toggle
                    label="Content API Enabled"
                    checked={!!values.contentApiOnOff}
                    onChange={(_, checked) => setFieldValue("contentApiOnOff", !!checked)}
                    disabled={disableInputs || !values.cobrandId}
                  />
                </div>

                <TextField
                  label="Key Vault Secret Name - Client ID"
                  value={values.contentApiClientKey}
                  onChange={(_, newValue) => setFieldValue("contentApiClientKey", newValue || "")}
                  disabled={disableInputs || !values.cobrandId}
                />

                <TextField
                  label="Key Vault Secret Name - Client Secret"
                  value={values.contentApiPrivateSecretKey}
                  onChange={(_, newValue) =>
                    setFieldValue("contentApiPrivateSecretKey", newValue || "")
                  }
                  className="mt10"
                  disabled={disableInputs || !values.cobrandId}
                />

                <TextField
                  label="API Key (SHA-256) Hash"
                  value={values.contentApiKeyHash}
                  onChange={(_, newValue) => setFieldValue("contentApiKeyHash", newValue || "")}
                  className="mt10"
                  disabled={disableInputs || !values.cobrandId}
                />

                <div className="mt20 panel panel-default">
                  <div className="panel-heading panel-heading-em">Generate Azure Secrets</div>
                  <div className="panel-body">
                    <p>
                      Generate a machine-to-machine Client ID, Client Secret, and the plain Content API key.
                      The SHA-256 hash field will be updated automatically. Values are shown only once—copy them
                      before closing the dialog and store them securely (for Azure Key Vault & customer handoff).
                    </p>
                    <PrimaryButton
                      className="mt10"
                      text="Generate Credentials"
                      disabled={!values.cobrandId || disableInputs || hashingApiKey}
                      onClick={async () => {
                        setCopyFeedback("");
                        setUploadMessage("");
                        setUploadError("");
                        const clientIdSecretName = buildSecretName("ClientId");
                        const clientSecretSecretName = buildSecretName("ClientSecret");
                        if (!clientIdSecretName || !clientSecretSecretName) {
                          setShowError(true);
                          setErrorMessage("Cobrand name is required to generate Key Vault secret names.");
                          return;
                        }
                        const secrets = {
                          clientId: generateClientId(),
                          clientSecret: generateClientSecret(),
                          apiKeyPlain: generateApiKey()
                        };
                        setGeneratedSecrets(secrets);
                        setShowSecretsDialog(true);
                        setHashingApiKey(true);
                        setFieldValue("contentApiClientKey", clientIdSecretName);
                        setFieldValue("contentApiPrivateSecretKey", clientSecretSecretName);
                        try {
                          const hashed = await hashSha256(secrets.apiKeyPlain);
                          setFieldValue("contentApiKeyHash", hashed);
                        } catch (err) {
                          console.error(err);
                          setShowError(true);
                          setErrorMessage("Unable to hash API key. Please try again.");
                        } finally {
                          setHashingApiKey(false);
                        }
                      }}
                    />
                    {hashingApiKey && <Spinner className="mt10" label="Hashing API key..." />}
                  </div>
                </div>

                <ContentApiWhitelistFields
                  values={values}
                  onChange={setFieldValue}
                  disabled={disableInputs || !values.cobrandId}
                />

                <div className="mt20">
                  <PrimaryButton
                    type="submit"
                    text="Save Settings"
                    disabled={!values.cobrandId || saving || isSubmitting}
                  />
                </div>
              </Form>

              <ContentApiSecretsDialog
                visible={showSecretsDialog && !!generatedSecrets}
                secrets={generatedSecrets}
                copyFeedback={copyFeedback}
                copyFeedbackType={copyFeedbackType}
                uploadMessage={uploadMessage}
                uploadError={uploadError}
                uploadingSecrets={uploadingSecrets}
                onCopy={handleCopy}
                onUpload={async () => {
                  if (!generatedSecrets) return;
                  const clientIdSecretName = values.contentApiClientKey?.trim();
                  const clientSecretName = values.contentApiPrivateSecretKey?.trim();
                  if (!clientIdSecretName || !clientSecretName) {
                    setUploadError("Enter the Key Vault secret names before uploading.");
                    return;
                  }
                  try {
                    setUploadError("");
                    setUploadMessage("");
                    await uploadSecrets({
                      data: {
                        cobrandId: values.cobrandId,
                        clientIdSecretName,
                        clientIdSecretValue: generatedSecrets.clientId,
                        clientSecretSecretName: clientSecretName,
                        clientSecretSecretValue: generatedSecrets.clientSecret
                      }
                    });
                    setUploadMessage("Secrets uploaded to Azure Key Vault.");
                  } catch (err) {
                    console.error(err);
                    setUploadError("Failed to upload secrets to Azure Key Vault.");
                  }
                }}
                onDismiss={closeSecretsDialog}
              />
            </>
          );
        }}
      </Formik>

      {(saving || loadingSettings) && <Spinner className="mt20" label="Working..." />}

      <Toast show={showToast} setShow={setShowToast} message="Content API settings saved" />
      <FeatureSummary
          label="About Content API Settings"
          summary="Configure Content API access for a cobrand. Generate client credentials, manage API keys, set IP and origin whitelists, and upload secrets to Azure Key Vault. Toggle the API on/off and control approved content restrictions."
      />
    </div>
  );
};

export default ContentApiSettings;
